/**
 * Loads conversation history once on mount and keeps the in-memory
 * history cache fresh for cheap re-visits.
 *
 * - First, prefer pre-fetched `initialMessages` (from /connect via
 *   `useAgUiState`) — no extra network round-trip.
 * - Otherwise fall back to the LRU history cache, then to a fresh
 *   `/connect` fetch.
 * - On every local run start, snapshots the current thread state into
 *   the cache so the next visit doesn't refetch a stale "running"
 *   message.
 *
 * Returns `{ isLoadingHistory, historyHasMessages, reloadHistory }`.
 * `reloadHistory` is the same fetch+cache+thread.reset path that
 * `useRemoteRun` calls after a replay or cancel settles.
 */
import { useCallback, useEffect, useState } from "react";
import type { HttpAgent } from "@ag-ui/client";
import type { AssistantRuntime, ThreadMessageLike } from "@assistant-ui/react";
import { fetchConnect } from "./agui-messages";
import { getCachedHistory, setCachedHistory } from "./history-cache";

interface UseHistoryLoadOptions {
  agent: HttpAgent;
  runtime: AssistantRuntime;
  baseUrl: string;
  conversationId: string;
  /** Pre-converted messages from /connect — skips the /messages_agui fetch. */
  initialMessages?: ThreadMessageLike[] | null;
}

interface UseHistoryLoadReturn {
  isLoadingHistory: boolean;
  historyHasMessages: boolean;
  reloadHistory: () => Promise<void>;
}

export function useHistoryLoad({
  agent,
  runtime,
  baseUrl,
  conversationId,
  initialMessages,
}: UseHistoryLoadOptions): UseHistoryLoadReturn {
  const [isLoadingHistory, setIsLoadingHistory] = useState(true);
  const [historyHasMessages, setHistoryHasMessages] = useState(false);

  const reloadHistory = useCallback(async () => {
    try {
      const payload = await fetchConnect(baseUrl, conversationId);
      const messages = payload?.messages ?? [];
      if (messages.length > 0) {
        setCachedHistory(conversationId, messages);
        runtime.thread.reset(messages);
      }
    } catch (error) {
      console.error("Failed to reload history:", error);
    }
  }, [baseUrl, conversationId, runtime]);

  // Initial load (mount only).
  useEffect(() => {
    let active = true;

    (async () => {
      let loaded = false;

      if (initialMessages && initialMessages.length > 0) {
        setCachedHistory(conversationId, initialMessages);
        runtime.thread.reset(initialMessages);
        loaded = true;
      } else if (!initialMessages) {
        // /connect had no messages or errored — try cache, then server
        const cached = getCachedHistory(conversationId);
        if (cached?.length) {
          runtime.thread.reset(cached);
          loaded = true;
        } else {
          try {
            const payload = await fetchConnect(baseUrl, conversationId);
            if (!active) return;
            const messages = payload?.messages ?? [];
            if (messages.length > 0) {
              setCachedHistory(conversationId, messages);
              runtime.thread.reset(messages);
              loaded = true;
            }
          } catch (error) {
            console.error("Failed to load conversation history:", error);
          }
        }
      }
      // initialMessages === [] means /connect returned no history — nothing to load

      if (active) {
        setHistoryHasMessages(loaded);
        setIsLoadingHistory(false);
      }
    })();

    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  // Snapshot the current thread to the cache whenever a local run
  // starts, so a subsequent visit doesn't refetch a stale "running"
  // message that was never persisted server-side.
  useEffect(() => {
    const cid = conversationId;
    const sub = agent.subscribe({
      onRunStartedEvent: () => {
        const currentMessages = runtime.thread.getState().messages;
        if (currentMessages.length > 0) {
          setCachedHistory(cid, [...currentMessages.filter((m) => m.status?.type !== "running")]);
        }
      },
    });
    return () => sub.unsubscribe();
    // eslint-disable-next-line react-hooks/exhaustive-deps -- mount only
  }, []);

  return { isLoadingHistory, historyHasMessages, reloadHistory };
}
